import type { UpdateCacheResponse } from "@stepfinance/transaction-history";
import { useQuery } from "@tanstack/react-query";

import { useEnrichedWallet } from "../../../contexts/enrichedWallet";
import { notify } from "../../../utils/notifications";
import { STEP_API_ENDPOINT } from "../../../utils/swr";
import { useStepAuthStore } from "../../stores/stepAuth";

type TransactionCacheStatus = UpdateCacheResponse;
// Add parameter for calling on a loop instead of just once

let error = false;

export const useTransactionCacheStatus = (callInLoop = false) => {
  const { publicKey, watching } = useEnrichedWallet();
  const jwt = useStepAuthStore((s) => s.jwt);

  const fetchTransactionCacheStatus = async () => {
    if (publicKey && !error && jwt) {
      const response = await fetch(
        `${STEP_API_ENDPOINT}/v1/transaction-history/update-cache?pubkey=${publicKey.toBase58()}`,
        { headers: { Authorization: `bearer ${jwt}` } },
      );

      if (!response.ok) {
        error = true;
        notify({
          type: "error",
          message:
            "Failed to perform transaction analysis on your wallet. Please refresh the page. If problem persists contact Step support in Discord.",
        });
        throw new Error("Failed to fetch transaction cache status");
      }

      const data: TransactionCacheStatus = await response.json();

      return data;
    }
  };

  const isScanComplete = (data: TransactionCacheStatus | undefined) => {
    if (data) {
      if (data.initialScanComplete || data.updateComplete) {
        return true;
      }
    }
    // TODO: Consider a refetch limit since it can fetch forever, but take into account that it can be a huge wallet
    return false;
  };

  return useQuery<TransactionCacheStatus | undefined>(
    ["transaction-cache-status", publicKey?.toBase58(), watching, jwt],
    fetchTransactionCacheStatus,
    {
      enabled: !!publicKey && !watching && Boolean(jwt),
      retry: true,
      retryOnMount: false,
      refetchInterval: (data) => {
        return callInLoop && !isScanComplete(data) && !error ? 2500 : false;
      },
    },
  );
};
